<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Modal from "$app/components/Modal.svelte"
  import DepartmentForm from "./DepartmentForm.svelte"
  import { SquarePen, Trash2, Menu, TriangleAlert } from "lucide-svelte"
  import EditDepartmentUsers from "./EditDepartmentUsers.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    department: DepartmentType
    onupdate: (department: DepartmentType) => void
    ondelete: (department: DepartmentType) => void
  }

  let props = $props()
  let { onupdate, ondelete }: Props = props
  let department = $state(props.department) as DepartmentType

  let saving = $state(false)
  let edit = $state(false)
  let editUsers = $state(false)
  let archive = $state(false)

  // Update department
  async function updateDepartment(form: DepartmentType) {
    try {
      saving = true

      const resp = await xhr.put(
        `/plans/${appStore.plan.id}/departments/${department.id}`,
        {
          name: form.name
        }
      )
      department.name = form.name

      onupdate(department)
      toast("Department was updated", "success")
      edit = false
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error updating the department", "error")
    } finally {
      saving = false
    }
  }
</script>

{#if edit}
  <DepartmentForm
    {department}
    ondata={(d) => updateDepartment(d)}
    oncancel={() => (edit = false)}
  />
{:else}
  <div class="flex" class:bg-blue-50={archive}>
    <div
      class="px-2 w-8 flex items-center justify-center bg-blue-50 hover:bg-blue-100 border-r border-gray-100"
    >
      <Menu class="size-5 text-gray-500 hover:text-gray-900" />
    </div>
    <div class="px-4 py-3 w-full">
      <div class="flex justify-between items-center">
        <div class="flex-1 whitespace-nowrap text-lg font-medium text-gray-900">
          {department.name}
        </div>
        <div class="whitespace-nowrap text-right">
          <button
            class="text-gray-500 hover:text-blue-500"
            title="Edit"
            onclick={() => (edit = true)}
          >
            <SquarePen class="size-5" />
          </button>
          <button
            type="button"
            class="pl-4 text-yellow-700"
            title="Archive department"
            onclick={() => {
              department._selected = true
              onupdate(department)
              archive = true
            }}
          >
            <Trash2 class="size-5" />
          </button>
        </div>
      </div>

      <div class="flex mt-3">
        <div class="">
          <div class="flex items-center">
            <div>
              {#if department.user_ids.length === 0}
                <div
                  class="text-gray-800 bg-gray-100 py-1 px-2.5 rounded-lg text-sm font-medium"
                >
                  No members
                </div>
              {:else}
                <div
                  class="text-gray-500 bg-indigo-100 px-2.5 py-1 rounded-lg text-sm font-medium flex items-center justify-center"
                >
                  <div
                    class="bg-indigo-800 h-4 w-4 text-white rounded-full flex items-center justify-center mr-1 text-xs"
                  >
                    {department.user_ids.length}
                  </div>
                  {department.user_ids.length === 1 ? "member" : "members"}
                </div>
              {/if}
            </div>

            <div class="ml-3">
              <button
                class="text-blue-500 text-sm cursor-pointer border border-gray-200 rounded-lg py-1 px-3 hover:bg-gray-50 hover:text-blue-600"
                onclick={() => (editUsers = true)}
              >
                {department.user_ids.length === 0
                  ? "Add members"
                  : "Edit members"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<Modal
  bind:open={archive}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
>
  <div class="p-4">
    <div class="flex items-center justify-center text-yellow-800 text-lg">
      <TriangleAlert class="size-6 mr-1" />
      <p>
        Are you sure you want to <span class="font-bold">archive</span> the selected
        department?
      </p>
    </div>

    <div class="p-2 mt-4 flex justify-center">
      <div class="text-yellow-600 bg-yellow-50 py-2 px-4 rounded">
        All related goals and tasks won't be visible once you archive the
        selected department.
      </div>
    </div>

    <div class="flex justify-center items-center gap-x-2 mt-4">
      <button
        class="btn btn-primary btn-lg font-semibold"
        onclick={() => {
          ondelete(department)
        }}
      >
        Archive
      </button>
      &nbsp;
      <button
        class="btn btn-secondary btn-lg font-semibold"
        onclick={() => (archive = false)}
      >
        Cancel
      </button>
    </div>
  </div>
</Modal>

<Modal
  bind:open={editUsers}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
>
  <EditDepartmentUsers
    {department}
    onupdate={(dept) => {
      department = dept
      editUsers = false
    }}
  />
</Modal>
